import React from 'react'
import Bugsnag from '@bugsnag/js'

export interface PagErrorBoundaryProps {
  children?: React.ReactNode
  /** Name used to identify the boundary in logs (e.g. the field name). */
  name?: string
  /** Node, or render function receiving the error and a reset callback. */
  fallback?: React.ReactNode | ((error: Error, reset: () => void) => React.ReactNode)
  /** When any value in this list changes, the boundary resets and tries to render children again. */
  resetKeys?: ReadonlyArray<unknown>
  onError?: (error: Error, info: React.ErrorInfo) => void
}

interface PagErrorBoundaryState {
  error: Error | null
}

const keysChanged = (a: ReadonlyArray<unknown> = [], b: ReadonlyArray<unknown> = []) =>
  a.length !== b.length || a.some((item, i) => !Object.is(item, b[i]))

const reportToBugsnag = (error: Error, name?: string) => {
  try {
    // Bugsnag is only started when enabled in config.json.
    if (Bugsnag.isStarted()) {
      Bugsnag.notify(error, (event) => {
        event.addMetadata('errorBoundary', { name: name || 'unnamed' })
      })
    }
  } catch (e) {}
}

class PagErrorBoundary extends React.Component<PagErrorBoundaryProps, PagErrorBoundaryState> {
  state: PagErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: any): PagErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[PagErrorBoundary${this.props.name ? `:${this.props.name}` : ''}]`, error, info)
    reportToBugsnag(error, this.props.name)
    try {
      this.props.onError?.(error, info)
    } catch (e) {}
  }

  componentDidUpdate(prevProps: PagErrorBoundaryProps) {
    if (this.state.error && keysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset()
    }
  }

  reset = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children ?? null
    const { fallback } = this.props
    if (typeof fallback === 'function') return fallback(error, this.reset)
    return fallback ?? null
  }
}

export default PagErrorBoundary
